import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, timeAgo } from "./shared";
import { ShieldCheck, Mail, Phone, Building2, Flag, ClipboardCheck, FileText, BadgeCheck } from "lucide-react";

const STATUSES = ["new", "contacted", "in_progress", "closed"];
const STATUS_CLS = {
  new: "bg-gold/15 text-gold border-gold/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  in_progress: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  closed: "bg-white/5 text-gray-500 border-white/10",
};

export function VeteranLeads() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifFilter, setVerifFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/veteran-leads");
      setData(data);
    } catch (e) {
      toast.error("Could not load veteran leads.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/veteran-leads/${id}/status`, { status });
      setData((d) => ({ ...d, interest: d.interest.map((r) => (r.id === id ? { ...r, status } : r)) }));
      toast.success("Status updated.");
    } catch (e) {
      toast.error("Update failed.");
    }
  };

  if (loading) return <Loader />;

  const interest = data?.interest || [];
  const priorReq = data?.prior_service_requests || [];
  const priorEst = data?.prior_service_estimates || [];
  const verifications = data?.verifications || [];
  const purchasedCount = verifications.filter((v) => v.used_at).length;
  const conv = verifications.length ? Math.round((purchasedCount / verifications.length) * 100) : 0;
  const filteredVerifs = verifications.filter((v) =>
    verifFilter === "all" ? true : verifFilter === "purchased" ? !!v.used_at : !v.used_at);
  const money = (a) => (a || a === 0) ? `$${Number(a).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "";
  const newCount = interest.filter((r) => (r.status || "new") === "new").length;
  const svcBadge = (s) => s === "transitioning"
    ? <span className="text-[10px] uppercase tracking-wider text-blue-300 border border-blue-500/30 bg-blue-500/15 px-1.5 py-0.5">Transitioning</span>
    : s === "veteran"
      ? <span className="text-[10px] uppercase tracking-wider text-gold border border-gold/30 bg-gold/15 px-1.5 py-0.5">Veteran</span>
      : null;

  return (
    <div data-testid="veteran-leads-section">
      <Head title="Veteran Leads" subtitle="Veterans Launch Initiative interest + any lead that flagged prior military service." />

      <div className="grid sm:grid-cols-4 gap-4 mb-8">
        {[["Initiative interest", interest.length, ShieldCheck], ["New / unactioned", newCount, Flag], ["Prior-service leads", priorReq.length + priorEst.length, ClipboardCheck], ["Verified veterans", verifications.length, BadgeCheck]].map(([l, v, Icon]) => (
          <div key={l} className="border border-white/10 bg-navy-800 p-5">
            <div className="flex items-center justify-between text-gray-400 text-xs uppercase tracking-wider mb-2"><span>{l}</span><Icon size={16} className="text-gold" /></div>
            <div className="text-3xl font-heading text-white">{v}</div>
          </div>
        ))}
      </div>

      {/* Initiative interest */}
      <h3 className="font-heading text-xl text-white mb-3 flex items-center gap-2"><ShieldCheck size={18} className="text-gold" /> Veterans Launch Initiative interest</h3>
      {interest.length === 0 ? <EmptyBox>No interest submissions yet.</EmptyBox> : (
        <div className="space-y-3 mb-10">
          {interest.map((r) => (
            <div key={r.id} className="border border-white/10 bg-navy-800 p-5" data-testid={`vet-interest-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold">{r.name}</span>
                    <span className={`text-[11px] px-2 py-0.5 border ${r.audience === "partner" ? "bg-violet-500/15 text-violet-300 border-violet-500/30" : "bg-gold/15 text-gold border-gold/30"}`}>
                      {r.audience === "partner" ? "Partner / Org" : "Service Member"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400 mt-1.5">
                    <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-gold"><Mail size={13} /> {r.email}</a>
                    {r.organization && <span className="flex items-center gap-1.5"><Building2 size={13} /> {r.organization}</span>}
                    {r.branch && <span className="flex items-center gap-1.5"><Flag size={13} /> {r.branch}</span>}
                  </div>
                  {r.message && <p className="text-gray-300 text-sm mt-2 bg-white/5 border border-white/10 px-3 py-2">{r.message}</p>}
                  <div className="text-xs text-gray-600 mt-2">{timeAgo(r.created_at)}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(r.id, s)} data-testid={`vet-status-${r.id}-${s}`}
                      className={`text-[11px] px-2.5 py-1 border capitalize transition-colors ${(r.status || "new") === s ? STATUS_CLS[s] : "border-white/10 text-gray-500 hover:text-gray-300"}`}>
                      {s.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Prior-service leads */}
      <h3 className="font-heading text-xl text-white mb-3 flex items-center gap-2"><ClipboardCheck size={18} className="text-gold" /> Leads who flagged prior military service</h3>
      {(priorReq.length + priorEst.length) === 0 ? <EmptyBox>No leads have flagged prior service yet.</EmptyBox> : (
        <div className="space-y-2">
          {priorReq.map((r) => (
            <div key={r.id} className="border border-white/10 bg-navy-800 p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`vet-prior-req-${r.id}`}>
              <div className="min-w-0">
                <div className="text-white text-sm font-medium flex items-center gap-2 flex-wrap"><FileText size={14} className="text-gold" /> {r.org_name || r.contact_name} <span className="text-[10px] uppercase tracking-wider text-gray-500 border border-white/10 px-1.5 py-0.5">Assessment</span> {svcBadge(r.service_status)}</div>
                <div className="flex flex-wrap gap-x-4 text-sm text-gray-400 mt-1">
                  <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-gold"><Mail size={13} /> {r.email}</a>
                  {r.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {r.phone}</span>}
                  {r.industry && <span>{r.industry}</span>}
                </div>
              </div>
              <div className="text-xs text-gray-600">{timeAgo(r.created_at)}</div>
            </div>
          ))}
          {priorEst.map((r) => (
            <div key={r.id} className="border border-white/10 bg-navy-800 p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`vet-prior-est-${r.id}`}>
              <div className="min-w-0">
                <div className="text-white text-sm font-medium flex items-center gap-2 flex-wrap"><FileText size={14} className="text-gold" /> {r.name} <span className="text-[10px] uppercase tracking-wider text-gray-500 border border-white/10 px-1.5 py-0.5">Estimate · {r.business_name}</span> {svcBadge(r.service_status)}</div>
                <div className="flex flex-wrap gap-x-4 text-sm text-gray-400 mt-1">
                  <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-gold"><Mail size={13} /> {r.email}</a>
                  {r.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {r.phone}</span>}
                  {r.service && <span>{r.service}</span>}
                </div>
              </div>
              <div className="text-xs text-gray-600">{timeAgo(r.created_at)}</div>
            </div>
          ))}
        </div>
      )}
      {/* Verified veterans */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 mt-10">
        <h3 className="font-heading text-xl text-white flex items-center gap-2"><BadgeCheck size={18} className="text-gold" /> Verified veterans &amp; issued codes</h3>
        {verifications.length > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400" data-testid="veteran-conversion">
              <span className="text-gold font-semibold">{purchasedCount}</span> of {verifications.length} purchased
              <span className="text-gray-500"> ({conv}%)</span>
            </span>
            <div className="flex gap-1.5">
              {[["all", "All"], ["not_purchased", "Not yet"], ["purchased", "Purchased"]].map(([k, l]) => (
                <button key={k} onClick={() => setVerifFilter(k)} data-testid={`verif-filter-${k}`}
                  className={`text-[11px] px-2.5 py-1 border transition-colors ${verifFilter === k ? "bg-gold/15 text-gold border-gold/30" : "border-white/10 text-gray-500 hover:text-gray-300"}`}>{l}</button>
              ))}
            </div>
          </div>
        )}
      </div>
      {verifications.length === 0 ? <EmptyBox>No one has verified their service yet.</EmptyBox> : (
        <div className="border border-white/10 bg-navy-800 overflow-x-auto" data-testid="veteran-verifications">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 text-xs uppercase tracking-wider border-b border-white/10">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Branch</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Purchased</th>
                <th className="px-4 py-3 font-medium">Verified</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filteredVerifs.map((v) => (
                <tr key={v.id} className="border-b border-white/5 last:border-0" data-testid={`vet-verif-${v.id}`}>
                  <td className="px-4 py-3 text-white">{v.name || <span className="text-gray-600">—</span>}</td>
                  <td className="px-4 py-3 text-gray-400">{v.email ? <a href={`mailto:${v.email}`} className="hover:text-gold">{v.email}</a> : <span className="text-gray-600">—</span>}</td>
                  <td className="px-4 py-3 text-gray-300">{v.branch || <span className="text-gray-600">—</span>}{v.years ? <span className="text-gray-500"> · {v.years} yrs</span> : null}</td>
                  <td className="px-4 py-3">{svcBadge(v.service_status) || <span className="text-gray-600">—</span>}</td>
                  <td className="px-4 py-3"><span className="font-mono text-gold text-xs">{v.code}</span></td>
                  <td className="px-4 py-3">
                    {v.used_at
                      ? <span className="text-[10px] uppercase tracking-wider text-emerald-300 border border-emerald-500/30 bg-emerald-500/15 px-1.5 py-0.5">Purchased{v.redeemed_amount ? ` · ${money(v.redeemed_amount)}` : ""}</span>
                      : <span className="text-[10px] uppercase tracking-wider text-gray-500 border border-white/10 px-1.5 py-0.5">Not yet</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{timeAgo(v.created_at)}</td>
                  <td className="px-4 py-3">
                    {!v.used_at && v.email && (
                      <a href={`mailto:${v.email}?subject=Your%20veteran%20discount%20at%20ATF%20WebWorks`} data-testid={`vet-followup-${v.id}`}
                        className="text-xs text-gold hover:text-gold-light whitespace-nowrap">Follow up →</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
